import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import {
  Box,
  Drawer,
  Typography,
  Divider,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
  ListItemButton,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  LibraryBooks as LibraryBooksIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Share as ShareIcon
} from '@mui/icons-material';

const drawerWidth = 280;
const collapsedDrawerWidth = 72;

const ChampionsNavigation = ({ 
  open, 
  onClose, 
  collapsed = false, 
  onToggleCollapse,
  supportOpen,
  onSupportOpen 
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Navigation menu items for champions
  const menuItems = [
    {
      group: 'Main',
      items: [
        { text: 'Dashboard', icon: <DashboardIcon />, path: '/champions/dashboard' }
      ]
    },
    {
      group: 'Content Management',
      items: [
        { text: 'Question Bank', icon: <LibraryBooksIcon />, path: '/champions/questionbank' },
        { text: 'Referral', icon: <ShareIcon />, path: '/champions/referral' }
      ]
    }
  ];

  const getMenuItems = () => {
    return menuItems;
  };

  const isActive = (path) => location.pathname === path;

  const renderMenuItem = (item) => {
    const active = isActive(item.path);
    
    const menuItem = (
      <ListItemButton
        key={item.text}
        onClick={() => navigate(item.path)}
        selected={active}
        sx={{
          borderRadius: 1,
          mx: 1,
          mb: 0.5,
          minHeight: 48,
          color: 'rgba(255, 255, 255, 0.7)',
          '&.Mui-selected': {
            backgroundColor: 'rgba(25, 118, 210, 0.16)',
            color: '#FFFFFF',
            fontWeight: 600,
            '&:hover': {
              backgroundColor: 'rgba(25, 118, 210, 0.24)',
            }
          },
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
            color: '#FFFFFF',
          }
        }}
      >
        <ListItemIcon sx={{ 
          minWidth: collapsed ? 40 : 48,
          color: active ? '#FFFFFF' : 'rgba(255, 255, 255, 0.7)'
        }}>
          {item.icon}
        </ListItemIcon>
        {!collapsed && (
          <ListItemText 
            primary={item.text}
            primaryTypographyProps={{
              fontSize: '0.875rem',
              fontWeight: active ? 600 : 400,
              color: active ? '#FFFFFF' : 'rgba(255, 255, 255, 0.7)'
            }}
          />
        )}
      </ListItemButton>
    );

    // Wrap with tooltip when collapsed
    if (collapsed) {
      return (
        <Tooltip 
          key={item.text}
          title={item.text} 
          placement="right"
          arrow
        >
          {menuItem}
        </Tooltip>
      );
    }

    return menuItem;
  };

  const renderMenuGroup = (group) => (
    <Box key={group.group}>
      {!collapsed ? (
        <Typography
          variant="caption"
          sx={{
            px: 3,
            py: 1,
            color: 'rgba(255, 255, 255, 0.5)',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            fontSize: '0.75rem'
          }}
        >
          {group.group}
        </Typography>
      ) : (
        // Show tooltip for group when collapsed
        <Tooltip title={group.group} placement="right" arrow>
          <Box sx={{ height: 16, mx: 1, mb: 0.5 }} />
        </Tooltip>
      )}
      {group.items.map(renderMenuItem)}
    </Box>
  );

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'space-between',
          p: 2,
          flexShrink: 0
        }}
      >
        {!collapsed ? (
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: '#FFFFFF',
              fontSize: '1.25rem'
            }}
          >
            Thinktyne
          </Typography>
        ) : (
          <Tooltip title="Thinktyne" placement="right" arrow>
            <Box sx={{ width: 24, height: 24 }} />
          </Tooltip>
        )}
        
        <Tooltip title={collapsed ? 'Expand Navigation' : 'Collapse Navigation'}>
          <IconButton
            onClick={onToggleCollapse}
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              '&:hover': {
                color: '#FFFFFF',
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
              }
            }}
          >
            {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Navigation Menu */}
      <Box sx={{ flexGrow: 1, py: 2, overflowY: 'auto' }}>
        {menuItems.map((group, index) => (
          <Box key={group.group}>
            {renderMenuGroup(group)}
            {index < menuItems.length - 1 && (
              <Divider sx={{ my: 2, mx: 2 }} />
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );

  return (
    <>
      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={open}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            backgroundColor: '#1a237e',
            color: '#FFFFFF',
            borderRight: '1px solid',
            borderColor: 'rgba(255, 255, 255, 0.12)',
            zIndex: theme.zIndex.drawer + 1
          },
        }}
        data-testid="champions-mobile-drawer"
      >
        {drawerContent}
      </Drawer>

      {/* Desktop Drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': {
            width: collapsed ? collapsedDrawerWidth : drawerWidth,
            backgroundColor: '#1a237e',
            color: '#FFFFFF',
            borderRight: '1px solid',
            borderColor: 'rgba(255, 255, 255, 0.12)',
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            overflow: 'hidden'
          },
        }}
        data-testid="champions-desktop-drawer"
      >
        {drawerContent}
      </Drawer>
    </>
  );
};

export default ChampionsNavigation;
